"use client"

import { useEffect, useRef, useState } from "react"
import Image from "next/image"
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Clock,
  DollarSign,
  Grid,
  Percent,
  SlidersHorizontal,
  SortAsc,
  Star,
  Tag,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { FilterButton } from "@/components/ui/filter-button"
import { Icons } from "@/components/icons"

interface Filter {
  label: string
  icon?: React.ReactNode
}

const filters: Filter[] = [
  { label: "Khuyến mại", icon: <Tag className="w-4 h-4" strokeWidth={2} /> },
  { label: "bePoint", icon: <Icons.bePoint /> },
  {
    label: "Siêu xịn",
    icon: (
      <Image
        src="https://imgcdn.be.com.vn/food/config/top_merchant_ranking.png"
        width={16}
        height={16}
        alt="Siêu xịn"
      />
    ),
  },
  { label: "Quán đối tác", icon: <Icons.partner className="text-green-600" /> },
  { label: "Đang mở cửa", icon: <Clock size={14} /> },
  { label: "Sắp xếp", icon: <SortAsc size={14} /> },
  { label: "Đánh giá", icon: <Star size={14} /> },
  { label: "Khoảng giá", icon: <DollarSign size={14} /> },
  { label: "Danh mục", icon: <Grid size={14} /> },
]

export function HomeFilters() {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showLeftButton, setShowLeftButton] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(true)

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current
      setShowLeftButton(scrollLeft > 0)
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1) // -1 for precision issues
    }
  }

  useEffect(() => {
    checkScrollButtons()
    const container = scrollContainerRef.current
    if (container) {
      container.addEventListener("scroll", checkScrollButtons)
      window.addEventListener("resize", checkScrollButtons)
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", checkScrollButtons)
        window.removeEventListener("resize", checkScrollButtons)
      }
    }
  }, [])

  const handleScroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = scrollContainerRef.current.clientWidth * 0.8
      scrollContainerRef.current.scrollBy({
        left: direction === "right" ? scrollAmount : -scrollAmount,
        behavior: "smooth",
      })
    }
  }

  return (
    <section className="container mx-auto">
      <div className="flex items-center gap-2">
        {/* Fixed Filter Button */}
        <FilterButton
          className="w-10 h-10 p-0 flex-shrink-0"
          icon={<SlidersHorizontal size={16} />}
          aria-label="Filter options"
        />

        {/* Scrollable Filters */}
        <div className="relative flex-1 overflow-hidden">
          <div
            ref={scrollContainerRef}
            className="flex space-x-2 overflow-x-auto scrollbar-hide py-1 pl-1"
          >
            {filters.map((filter, index) => (
              <FilterButton
                key={index}
                label={filter.label}
                icon={filter.icon}
                className="flex-shrink-0"
              />
            ))}
          </div>

          {/* Scroll Buttons */}
          {showLeftButton && (
            <button
              onClick={() => handleScroll("left")}
              className={cn(
                "absolute left-0 top-1/2 -translate-y-1/2 z-10",
                "p-1 bg-white rounded-full shadow-md",
                "focus:outline-none focus:ring-2 focus:ring-gray-400/50"
              )}
              aria-label="Scroll left"
            >
              <ChevronLeftIcon className="h-4 w-4 text-gray-700" />
            </button>
          )}

          {canScrollRight && (
            <button
              onClick={() => handleScroll("right")}
              className={cn(
                "absolute right-0 top-1/2 -translate-y-1/2 z-10",
                "p-1 bg-white rounded-full shadow-md",
                "focus:outline-none focus:ring-2 focus:ring-gray-400/50"
              )}
              aria-label="Scroll right"
            >
              <ChevronRightIcon className="h-4 w-4 text-gray-700" />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
